import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Consecutive animation frames with an unchanged document height required
// before a POP restore is considered "safe to apply" — see the effect below
// for why this can't just be a fixed couple of frames.
const STABLE_FRAMES_REQUIRED = 3;
// Hard ceiling on how many frames the restore will wait for layout to settle
// before giving up and applying `saved` anyway, so a page whose height never
// truly stops changing (e.g. a live-updating widget) can't hold the
// recording guard open indefinitely.
const MAX_SETTLE_FRAMES = 45;

const ScrollToTop = () => {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();

  // Keyed by react-router's location.key (unique per history entry) —
  // not pathname, which would collide across repeat visits to the same
  // route with different scroll positions.
  const scrollPositions = useRef(new Map<string, number>());

  // Id for the in-flight settle-and-restore rAF loop, so a rapid
  // Back-then-Forward (or any re-trigger of the POP effect before the
  // previous restore fired) cancels the stale chain instead of leaving two
  // independent restores racing to be "last write wins." Follows the same
  // ref-id + cancelAnimationFrame pattern as RotatingEarth.tsx's
  // `applyZoom`/`zoomAnimRef`.
  const settleRafRef = useRef<number | null>(null);

  // Guards the key currently being restored to. While set, the position
  // recording listener below must not overwrite this key's saved value —
  // see the useLayoutEffect for why that race exists.
  const restoringKeyRef = useRef<string | null>(null);

  const cancelPendingRestore = () => {
    if (settleRafRef.current !== null) {
      cancelAnimationFrame(settleRafRef.current);
      settleRafRef.current = null;
    }
  };

  useEffect(() => cancelPendingRestore, []);

  // Take exclusive control of scroll restoration, once. Left alone, the
  // browser's native `scrollRestoration` doesn't cooperate with this SPA's
  // client-side routing: Back lands at the *departing* page's
  // leftover scroll value, not the target page's own prior position.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Continuously record the scroll position of the *current* history entry
  // so it's accurate whenever we later leave it. We can't reliably grab it
  // in a one-shot "before navigation" hook: by the time any cleanup runs,
  // the DOM has already swapped to the new route's content, and the browser
  // may have already auto-clipped window.scrollY to fit the new (shorter)
  // page — which is exactly the leftover-value bug this fix addresses.
  //
  // This must be useLayoutEffect, not useEffect: a plain effect is deferred
  // to a passive phase after paint, leaving a window where the *old* key's
  // listener is still attached (or no listener is attached yet) even though
  // React has already committed the new route. A scroll event fired in that
  // window would record against the wrong history entry. useLayoutEffect
  // swaps the listener synchronously with the route commit, so there's no
  // gap in which a scroll event can be misattributed.
  useLayoutEffect(() => {
    // Mark this key as "restoring" synchronously, in the same commit that
    // attaches its listener — not in the restore effect below, which is a
    // plain (passive) effect deferred until after paint. A POP navigation to
    // a page shorter than the one just left can trigger the browser's native
    // scroll-clamp, which dispatches its own `scroll` event asynchronously,
    // after paint. If the guard were set later (in the restore effect), that
    // clamp event could sneak in first and get recorded as this key's "real"
    // position, clobbering the actual saved value before the restore ever
    // reads it. Setting the guard here, before the listener is even
    // attached, closes that window entirely.
    if (navigationType === "POP") {
      restoringKeyRef.current = key;
    }

    const positions = scrollPositions.current;
    const recordPosition = () => {
      if (restoringKeyRef.current === key) return;
      positions.set(key, window.scrollY);
    };
    window.addEventListener("scroll", recordPosition, { passive: true });
    return () => {
      window.removeEventListener("scroll", recordPosition);

      // The browser dispatches a scroll's `scroll` event asynchronously —
      // at its next rendering opportunity, not synchronously with the
      // scroll itself. A scroll that happens right before navigating away
      // can have its event still undispatched at this exact moment: React
      // Router's history change, this effect's teardown, and the new
      // route's commit can all happen within one synchronous JS turn, with
      // no rendering opportunity in between for the browser to fire it —
      // so `recordPosition` above never runs for it, and this key's last
      // real scroll position is lost, falling back to a stale (often 0)
      // value the next time it's restored. Flush the current value
      // directly as a last resort so it's never lost.
      //
      // This read happens after the new route's DOM has already committed
      // (same reasoning as the comment above this effect), so if the new
      // page is shorter, the browser may have already clamped scrollY down
      // to fit it. A smaller reading here doesn't mean the user actually
      // scrolled up — scrollY only ever gets clamped down, never up — so
      // only trust this flush when it's at least as large as what's
      // already recorded; otherwise keep the continuously-recorded value,
      // which is the exact leftover-value corruption this file exists to
      // avoid.
      if (restoringKeyRef.current !== key) {
        const flushed = window.scrollY;
        const recorded = positions.get(key) ?? 0;
        if (flushed >= recorded) {
          positions.set(key, flushed);
        }
      }
    };
  }, [key, navigationType]);

  useEffect(() => {
    if (navigationType === "POP") {
      // Cancel any restore chain still in flight from a previous POP (e.g. a
      // rapid Back-then-Forward before the previous restore settled) so only
      // the most recent navigation's restore ever lands.
      cancelPendingRestore();
      const saved = scrollPositions.current.get(key) ?? 0;

      // Target content (images, async data — e.g. a destination query feeding
      // the homepage globe/picks) may not have reached its final height yet.
      // A fixed number of frames can't be trusted to cover that: it only has
      // to be "long enough" in whatever environment loaded the fixture data
      // used to tune it, and real network-loaded content can keep growing
      // well past a couple of frames. Instead, poll actual layout height and
      // only restore (and release the recording guard above) once it's held
      // steady for a few consecutive frames — capped so a page whose height
      // never truly settles can't wedge the guard open forever.
      let lastHeight = -1;
      let stableFrames = 0;
      let framesWaited = 0;

      const waitForLayoutToSettle = () => {
        settleRafRef.current = null;
        framesWaited += 1;
        const height = document.documentElement.scrollHeight;
        if (height === lastHeight) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
          lastHeight = height;
        }
        const settled = stableFrames >= STABLE_FRAMES_REQUIRED;
        const gaveUp = framesWaited >= MAX_SETTLE_FRAMES;
        if (settled || gaveUp) {
          window.scrollTo(0, saved);
          if (restoringKeyRef.current === key) {
            restoringKeyRef.current = null;
          }
          return;
        }
        settleRafRef.current = requestAnimationFrame(waitForLayoutToSettle);
      };
      settleRafRef.current = requestAnimationFrame(waitForLayoutToSettle);
      return;
    }

    // A non-POP navigation supersedes any restore still in flight for a
    // previous key.
    cancelPendingRestore();
    restoringKeyRef.current = null;

    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView();
        return;
      }
    }

    window.scrollTo(0, 0);
  }, [pathname, hash, navigationType, key]);

  return null;
};

export default ScrollToTop;
